import { DateTime } from "luxon";

const SCHEDULE_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export type TimeOffType = "pto" | "emergency" | "other";

export interface ScheduleDay {
  day: string;
  isOnline: boolean;
  startTime: string;
  endTime: string;
}

export interface TimeOffEntry {
  id: string;
  type: TimeOffType;
  note: string | null;
  startsAt: string;
  endsAt: string;
  endedEarlyAt: string | null;
  podTimezone: string;
}

const DAY_NUMBER: Record<string, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

const ZONE_NAMES: Record<string, string> = {
  "America/New_York": "Eastern Time",
  "America/Chicago": "Central Time",
  "America/Denver": "Mountain Time",
  "America/Los_Angeles": "Pacific Time",
  "America/Anchorage": "Alaska Time",
  "America/Adak": "Aleutian Time",
  "Pacific/Honolulu": "Hawaii Time",
};

export function zoneName(zone: string): string {
  return ZONE_NAMES[zone] ?? zone;
}

export function zoneLabel(zone: string, at: DateTime = DateTime.now()): string {
  const abbr = at.setZone(zone).offsetNameShort ?? zone;
  return `${zoneName(zone)} (${abbr})`;
}

export function zoneAbbreviation(
  zone: string,
  at: DateTime = DateTime.now(),
): string {
  return at.setZone(zone).offsetNameShort ?? zone;
}

export function formatClock(time: string): string {
  return DateTime.fromFormat(time, "HH:mm").toFormat("h:mm a");
}

export function timeChoices(includeElevenFiftyNine: boolean): {
  value: string;
  label: string;
}[] {
  const choices: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of [0, 30]) {
      const value = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      choices.push({ value, label: formatClock(value) });
    }
  }
  if (includeElevenFiftyNine) {
    choices.push({ value: "23:59", label: "11:59 PM" });
  }
  return choices;
}

function clockOnDay(
  day: string,
  time: string,
  zone: string,
  at: DateTime,
): DateTime {
  const weekday = DAY_NUMBER[day] ?? 1;
  const [hour, minute] = time.split(":").map(Number);
  const zoned = at.setZone(zone);
  return zoned
    .plus({ days: weekday - zoned.weekday })
    .set({ hour, minute, second: 0, millisecond: 0 });
}

const toMinutes = (time: string): number => {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
};

// A row belongs to the day it starts on. An end at or before the start is on the next day.
const rowMinutes = (day: ScheduleDay): number => {
  const minutes = toMinutes(day.endTime) - toMinutes(day.startTime);
  return minutes <= 0 ? minutes + 24 * 60 : minutes;
};

const calendarDays = (from: DateTime, to: DateTime): number =>
  DateTime.fromISO(to.toISODate() ?? "", { zone: "utc" }).diff(
    DateTime.fromISO(from.toISODate() ?? "", { zone: "utc" }),
    "days",
  ).days;

export function formatHoursInZone(
  day: ScheduleDay,
  fromZone: string,
  toZone: string,
  at: DateTime = DateTime.now(),
): string {
  if (!day.isOnline) return "Off";
  const podStart = clockOnDay(day.day, day.startTime, fromZone, at);
  const start = podStart.setZone(toZone);
  const end = podStart.plus({ minutes: rowMinutes(day) }).setZone(toZone);
  const startLabel = start.toFormat("h:mm a");
  const endLabel = end.toFormat("h:mm a");
  // A shift ending at midnight still reads as that day's shift.
  const sameLocalDay =
    start.toISODate() === end.minus({ minutes: 1 }).toISODate();
  const shifted = start.toISODate() !== podStart.toISODate();
  if (sameLocalDay && !shifted) {
    return `${startLabel} – ${endLabel}`;
  }
  if (sameLocalDay) {
    return `${start.toFormat("ccc")} ${startLabel} – ${endLabel}`;
  }
  const startPrefix = shifted ? `${start.toFormat("ccc")} ` : "";
  return `${startPrefix}${startLabel} – ${end.toFormat("ccc")} ${endLabel}`;
}

export interface EditableDay extends ScheduleDay {
  // Days between the row's weekday and its local start, e.g. 1 when Monday 1pm Chicago is Tuesday in Kolkata.
  shift: number;
}

export function weekForEditing(
  days: ScheduleDay[],
  savedZone: string,
  localZone: string,
  at: DateTime = DateTime.now(),
): EditableDay[] {
  return days.map((day) => {
    const savedStart = clockOnDay(day.day, day.startTime, savedZone, at);
    const start = savedStart.setZone(localZone);
    const end = savedStart
      .plus({ minutes: rowMinutes(day) })
      .setZone(localZone);
    return {
      ...day,
      startTime: start.toFormat("HH:mm"),
      endTime: end.toFormat("HH:mm"),
      shift: calendarDays(savedStart, start),
    };
  });
}

export type SavedWeek =
  | { hours: ScheduleDay[]; timezone: string }
  | { clash: string };

const weekdayName = (at: DateTime): string =>
  at.setLocale("en-US").toFormat("cccc");

interface DatedRow {
  day: ScheduleDay;
  start: DateTime;
}

const datedRows = (
  days: EditableDay[],
  localZone: string,
  savedZone: string,
  at: DateTime,
): DatedRow[] =>
  days.map(({ shift, ...day }) => {
    const savedDate = clockOnDay(day.day, "00:00", savedZone, at).toISODate();
    const [hour, minute] = day.startTime.split(":").map(Number);
    const start = DateTime.fromISO(savedDate ?? "", { zone: localZone })
      .plus({ days: shift })
      .set({ hour, minute, second: 0, millisecond: 0 });
    return { day, start };
  });

const keyedByLocalDay = (rows: DatedRow[], zone: string): SavedWeek => {
  const byDay = new Map<string, ScheduleDay>();
  for (const { day, start } of rows) {
    if (!day.isOnline) continue;
    const localDay = weekdayName(start);
    if (byDay.has(localDay)) return { clash: localDay };
    byDay.set(localDay, { ...day, day: localDay });
  }
  return {
    timezone: zone,
    hours: SCHEDULE_DAYS.map((name) => {
      const online = byDay.get(name);
      if (online) return online;
      const placeholder = rows.find(({ day }) => day.day === name)?.day;
      return {
        day: name,
        isOnline: false,
        startTime: placeholder?.startTime ?? "17:00",
        endTime: placeholder?.endTime ?? "19:00",
      };
    }),
  };
};

export function weekInZone(
  days: ScheduleDay[],
  fromZone: string,
  toZone: string,
  at: DateTime = DateTime.now(),
): SavedWeek {
  return keyedByLocalDay(
    datedRows(weekForEditing(days, fromZone, toZone, at), toZone, fromZone, at),
    toZone,
  );
}

// Each row can only hold a shift that starts on its own weekday. Keep the saved zone
// when that still holds there; otherwise save in the edited zone (Monday 10pm Pacific
// is Tuesday in Chicago, so a Chicago week has no Monday row for it).
export function weekFromEditing(
  days: EditableDay[],
  localZone: string,
  savedZone: string,
  at: DateTime = DateTime.now(),
): SavedWeek {
  const rows = datedRows(days, localZone, savedZone, at);
  const savedStays = rows.every(
    ({ day, start }) =>
      !day.isOnline || weekdayName(start.setZone(savedZone)) === day.day,
  );
  if (savedStays) {
    return {
      timezone: savedZone,
      hours: rows.map(({ day, start }) => {
        const savedStart = start.setZone(savedZone);
        return {
          ...day,
          startTime: savedStart.toFormat("HH:mm"),
          endTime: savedStart
            .plus({ minutes: rowMinutes(day) })
            .toFormat("HH:mm"),
        };
      }),
    };
  }
  return keyedByLocalDay(rows, localZone);
}

export function localDayTag(
  day: EditableDay,
  edge: "start" | "end",
): string | null {
  const endsNextDay =
    edge === "end" &&
    day.endTime !== "00:00" &&
    toMinutes(day.endTime) <= toMinutes(day.startTime);
  const offset = day.shift + (endsNextDay ? 1 : 0);
  if (offset === 0) return null;
  const index = (DAY_NUMBER[day.day] ?? 1) - 1 + offset;
  return SCHEDULE_DAYS[(index + 7) % 7].slice(0, 3);
}

export function formatPodHours(day: ScheduleDay): string {
  if (!day.isOnline) return "Off";
  return `${formatClock(day.startTime)} – ${formatClock(day.endTime)}`;
}

function ordinal(day: number): string {
  const mod = day % 100;
  if (mod >= 11 && mod <= 13) return `${day}th`;
  if (day % 10 === 1) return `${day}st`;
  if (day % 10 === 2) return `${day}nd`;
  if (day % 10 === 3) return `${day}rd`;
  return `${day}th`;
}

export function displayEnd(iso: string, zone: string): DateTime {
  const end = DateTime.fromISO(iso, { setZone: true }).setZone(zone);
  if (end.hour === 0 && end.minute === 0 && end.second === 0) {
    return end.minus({ minutes: 1 });
  }
  return end;
}

function longDate(value: DateTime): string {
  return `${value.toFormat("cccc, LLLL")} ${ordinal(value.day)}`;
}

export function timeOffKind(type: TimeOffType): string {
  if (type === "pto") return "PTO";
  if (type === "emergency") return "Emergency time off";
  return "Time off";
}

export function timeOffBadge(type: TimeOffType): string {
  if (type === "pto") return "PTO";
  if (type === "emergency") return "Emergency";
  return "Other";
}

export function currentBanner(
  entry: TimeOffEntry,
  zone = entry.podTimezone,
): string {
  const start = DateTime.fromISO(entry.startsAt, { setZone: true }).setZone(
    zone,
  );
  const end = displayEnd(entry.endsAt, zone);
  const kind = timeOffKind(entry.type);
  if (start.hasSame(end, "day")) {
    return `Mentor off today. ${kind} scheduled on ${longDate(start)}.`;
  }
  return `Mentor off today. ${kind} scheduled from ${longDate(start)} to ${longDate(end)}.`;
}

export function formatWindow(
  entry: TimeOffEntry,
  zone = entry.podTimezone,
): string {
  const start = DateTime.fromISO(entry.startsAt, { setZone: true }).setZone(
    zone,
  );
  const end = displayEnd(entry.endsAt, zone);
  const abbr = zoneAbbreviation(zone, start);
  return `${start.toFormat("ccc, LLL d, h:mm a")} – ${end.toFormat("ccc, LLL d, h:mm a")} ${abbr}`;
}

export function canceledLine(
  entry: TimeOffEntry,
  zone = entry.podTimezone,
): string | null {
  if (!entry.endedEarlyAt) return null;
  const ended = DateTime.fromISO(entry.endedEarlyAt, { setZone: true }).setZone(
    zone,
  );
  const abbr = zoneAbbreviation(zone, ended);
  return `Canceled and back ${ended.toFormat("ccc, LLL d")} at ${ended.toFormat("h:mm a")} ${abbr}`;
}

export interface TimeOffDraft {
  type: TimeOffType;
  note: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
}

export function draftInstants(
  draft: TimeOffDraft,
  zone: string,
): { start: DateTime; end: DateTime } | null {
  if (!draft.startDate || !draft.endDate) return null;
  const start = combine(draft.startDate, draft.startTime, zone);
  let end = combine(draft.endDate, draft.endTime, zone);
  if (!start.isValid || !end.isValid) return null;
  if (end.hour === 23 && end.minute === 59) end = end.plus({ minutes: 1 });
  return { start, end };
}

function combine(isoDate: string, time: string, zone: string): DateTime {
  const [year, month, day] = isoDate.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  return DateTime.fromObject(
    { year, month, day, hour, minute, second: 0, millisecond: 0 },
    { zone },
  );
}

export function previewCopy(draft: TimeOffDraft, zone: string): string {
  const window = draftInstants(draft, zone);
  if (!window) {
    return "Pick a start date to see exactly when your time off begins and ends.";
  }
  const shownEnd =
    draft.endTime === "23:59" ? window.end.minus({ minutes: 1 }) : window.end;
  const abbr = zoneAbbreviation(zone, window.start);
  const kind =
    draft.type === "pto"
      ? "PTO"
      : draft.type === "emergency"
        ? "emergency time off"
        : "time off";
  return `Your ${kind} will start ${window.start.toFormat("cccc, LLLL d")} at ${window.start.toFormat("h:mm a")} ${abbr} and end ${shownEnd.toFormat("cccc, LLLL d")} at ${shownEnd.toFormat("h:mm a")} ${abbr}.`;
}

export function defaultTimeOffDates(
  zone: string,
  now = DateTime.now(),
): { startDate: string; endDate: string } {
  const today = now.setZone(zone);
  return {
    startDate: today.toISODate() ?? "",
    endDate: today.plus({ days: 1 }).toISODate() ?? "",
  };
}

export function overlapMessage(entry: TimeOffEntry): string {
  return `These dates overlap with ${timeOffKind(entry.type)} you already have scheduled (${formatWindow(entry)}). Edit or cancel that entry in Upcoming below, then try again.`;
}

export function findOverlap(
  draft: TimeOffDraft,
  entries: TimeOffEntry[],
  zone: string,
  ignoreId?: string,
): TimeOffEntry | null {
  const window = draftInstants(draft, zone);
  if (!window || window.end <= window.start) return null;
  for (const entry of entries) {
    if (entry.id === ignoreId || entry.endedEarlyAt) continue;
    const start = DateTime.fromISO(entry.startsAt, { setZone: true });
    const end = DateTime.fromISO(entry.endsAt, { setZone: true });
    if (window.start < end && window.end > start) return entry;
  }
  return null;
}

export function pickerDate(isoDate: string): Date {
  return DateTime.fromISO(isoDate).toJSDate();
}

export function isoFromPicker(date: Date): string {
  return DateTime.fromJSDate(date).toISODate() ?? "";
}
